import { getSrc } from '@/services/media.js'
import { buildRendererPropsFromItem } from '@/utils/dsItemRendererProps.js'
import { resolveWallCollisions } from '@/utils/designStudioCollision.js'

export const PROJECT_WALL_ITEM_LIMIT = 100
export const PROJECT_WALL_GAP_INCHES = 12
export const PROJECT_WALL_SIDE_PADDING_INCHES = 240
export const PROJECT_WALL_MIN_WIDTH_INCHES = 32 * 12
export const PROJECT_WALL_EYE_LEVEL_INCHES = 57

function normalizeId(value) {
  if (value == null || value === '') return null
  return String(value)
}

function projectItemKey(item) {
  return `project-item:${item?.id ?? 'unknown'}`
}

function parsePositiveNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function getRenderItemWidth(renderItem) {
  const props = renderItem?.rendererProps || {}
  return parsePositiveNumber(props.targetWidth, parsePositiveNumber(props.artWidth, 16))
}

function getRenderItemHeight(renderItem) {
  const props = renderItem?.rendererProps || {}
  return parsePositiveNumber(props.targetHeight, parsePositiveNumber(props.artHeight, 16))
}

// Push overlapping items apart in place (settle/commit mode) so the resolved layout has no
// overlaps, keeping the selected item(s) pinned. `pinnedItemId` accepts a single id or an
// array (multi-item drags pin the whole moved group). Operates on the wall-relative
// position.x/y the items already carry, and returns a new array with updated positions
// (footprints stay put).
function separateWallRenderItems(resolvedItems, { pinnedKey = null, pinnedItemId = null } = {}) {
  if (!Array.isArray(resolvedItems) || resolvedItems.length < 2) return resolvedItems

  const normalizedPinnedKey = pinnedKey == null ? null : String(pinnedKey)
  const pinnedIds = new Set(
    (Array.isArray(pinnedItemId) ? pinnedItemId : [pinnedItemId])
      .map(normalizeId)
      .filter(id => id != null)
  )

  const boxes = resolvedItems.map((item) => {
    const x = Number(item?.position?.x) || 0
    const y = Number(item?.position?.y) || 0
    const itemId = normalizeId(item?.itemId)
    const pinned = (normalizedPinnedKey != null && String(item?.key) === normalizedPinnedKey)
      || pinnedIds.has(itemId)
    return {
      key: item?.key,
      x,
      y,
      halfW: getRenderItemWidth(item) / 2,
      halfH: getRenderItemHeight(item) / 2,
      pinned,
      anchorX: x,
      anchorY: y,
    }
  })

  resolveWallCollisions(boxes) // settle mode (full separation, no gravity)

  return resolvedItems.map((item, index) => {
    const box = boxes[index]
    if (box.x === item.position?.x && box.y === item.position?.y) return item
    return { ...item, position: { ...item.position, x: box.x, y: box.y } }
  })
}

export function getProjectWallLayoutFromSceneXs(
  sceneXs = [],
  minWallWidth = PROJECT_WALL_MIN_WIDTH_INCHES,
) {
  const xs = (Array.isArray(sceneXs) ? sceneXs : [])
    .map(value => Number(value))
    .filter(Number.isFinite)

  if (!xs.length) {
    const wallWidth = parsePositiveNumber(minWallWidth, PROJECT_WALL_MIN_WIDTH_INCHES)
    return {
      wallWidth,
      wallCenterX: 0,
      wallLeftX: -wallWidth / 2,
    }
  }

  const firstX = xs[0]
  const lastX = xs[xs.length - 1]
  const span = Math.abs(lastX - firstX)
  const wallWidth = Math.max(
    parsePositiveNumber(minWallWidth, PROJECT_WALL_MIN_WIDTH_INCHES),
    span + PROJECT_WALL_SIDE_PADDING_INCHES * 2,
  )
  const wallCenterX = (firstX + lastX) / 2
  return {
    wallWidth,
    wallCenterX,
    wallLeftX: wallCenterX - wallWidth / 2,
  }
}

function applyWallPositions(renderItems, gapInches = PROJECT_WALL_GAP_INCHES) {
  const gap = parsePositiveNumber(gapInches, PROJECT_WALL_GAP_INCHES)

  // Compute the natural auto-layout position for every item as if nothing were pinned.
  // This keeps unpinned items stable when other items are moved.
  let cursor = 0
  const naturalX = renderItems.map((renderItem, index) => {
    const width = getRenderItemWidth(renderItem)
    const x = index === 0 ? 0 : cursor + gap + (width / 2)
    cursor = x + (width / 2)
    return x
  })
  const wallLayout = getProjectWallLayoutFromSceneXs(naturalX)

  return renderItems.map((renderItem, index) => {
    if (renderItem.wallPositionSet) {
      return { ...renderItem, naturalX: naturalX[index], wallSpacePosition: true }
    }
    return {
      ...renderItem,
      naturalX: naturalX[index],
      wallSpacePosition: true,
      position: {
        ...renderItem.position,
        x: naturalX[index] - wallLayout.wallLeftX,
        y: PROJECT_WALL_EYE_LEVEL_INCHES,
        z: 0,
      },
    }
  })
}

const SELECTED_AFFORDANCES = { resize: true, source: true, dimensions: true, snapOverlay: true }

export function buildSavedRenderItem({
  item,
  active = false,
  selected = active,
  affordances = null,
  availableOptions = [],
} = {}) {
  if (!item) return null
  const rendererProps = buildRendererPropsFromItem(item, availableOptions)
  const finishedWidth = Number(item.width)
  const finishedHeight = Number(item.height)

  const wallX = item.config_data?.wall_offset_x
  const wallY = item.config_data?.wall_offset_y
  const wallPositionSet = Number.isFinite(Number(wallX)) && Number.isFinite(Number(wallY))

  return {
    key: projectItemKey(item),
    itemId: item.id,
    selected: Boolean(selected),
    // Compatibility for older call sites/tests while FrameRenderer moves to selected specs.
    active: Boolean(selected),
    affordances: affordances || {},
    position: {
      x: wallPositionSet ? Number(wallX) : 0,
      y: wallPositionSet ? Number(wallY) : 0,
      z: 0,
    },
    wallPositionSet,
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    rendererProps: {
      ...rendererProps,
      targetWidth: Number.isFinite(finishedWidth) && finishedWidth > 0 ? finishedWidth : null,
      targetHeight: Number.isFinite(finishedHeight) && finishedHeight > 0 ? finishedHeight : null,
    },
    thumbnailUrl: item.thumbnail || getSrc(item.imagedata, 'imgsrc') || getSrc(item.imagedata) || null,
  }
}

export function buildLiveRenderItem({
  itemId,
  key,
  rendererProps,
  active = true,
  selected = active,
  affordances = null,
} = {}) {
  if (!rendererProps) return null
  return {
    key: key || `live-item:${itemId ?? 'new'}`,
    itemId,
    selected: Boolean(selected),
    active: Boolean(selected),
    affordances: affordances || {},
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    rendererProps: { ...rendererProps },
  }
}

export function buildProjectWallRenderItems({
  projectItems = [],
  availableOptions = [],
  limit = PROJECT_WALL_ITEM_LIMIT,
} = {}) {
  if (!Array.isArray(projectItems) || !projectItems.length) return []

  const selected = projectItems.slice(0, limit)
  return selected.map(item =>
    buildSavedRenderItem({
      item,
      selected: false,
      affordances: {},
      availableOptions,
    })
  ).filter(Boolean)
}

export function resolveProjectWallRenderItems({
  renderItems = [],
  selectedItemId = null,
  // 2+ ids switch to multi-selection: every matching spec is marked selected,
  // but interaction affordances and live renderer props only ever apply to a
  // single selection. 0-1 ids fall back to legacy single-id behavior.
  selectedItemIds = null,
  selectedRendererItemId = null,
  selectedRendererProps = null,
  gapInches = PROJECT_WALL_GAP_INCHES,
  collisionEnabled = false,
  pinnedItemId = undefined,
} = {}) {
  const selectedId = normalizeId(selectedItemId)
  const multiIds = new Set(
    (Array.isArray(selectedItemIds) ? selectedItemIds : [])
      .map(normalizeId)
      .filter(id => id != null)
  )
  const multi = multiIds.size >= 2
  const selectedRendererId = normalizeId(selectedRendererItemId)
  // Selection is only viewer state; it must not decide collision layout. Pinning is reserved for
  // explicit layout commits, such as holding a just-moved item where the user dropped it.
  const pinnedId = pinnedItemId === undefined ? null : pinnedItemId
  const baseItems = Array.isArray(renderItems)
    ? renderItems.filter(item => item && item.rendererProps)
    : []

  if (!baseItems.length) {
    const live = buildLiveRenderItem({
      itemId: selectedItemId,
      rendererProps: selectedRendererProps,
      selected: true,
      affordances: { ...SELECTED_AFFORDANCES },
    })
    return live ? applyWallPositions([live], gapInches) : []
  }

  const resolvedItems = baseItems.map(item => {
    const itemId = normalizeId(item.itemId)
    const isSelected = multi
      ? multiIds.has(itemId)
      : (selectedId != null && itemId === selectedId)
    const useLiveProps = Boolean(
      !multi
      && isSelected
      && selectedRendererProps
      && selectedRendererId != null
      && selectedRendererId === itemId
    )

    return {
      ...item,
      selected: isSelected,
      active: isSelected,
      affordances: isSelected && !multi ? { ...SELECTED_AFFORDANCES } : {},
      rendererProps: useLiveProps ? { ...selectedRendererProps } : item.rendererProps,
    }
  })

  const positioned = applyWallPositions(resolvedItems, gapInches)
  if (!collisionEnabled) return positioned
  return separateWallRenderItems(positioned, { pinnedItemId: pinnedId })
}

export function getActiveRenderItem(renderItems = []) {
  return getSelectedRenderItem(renderItems)
}

export function getSelectedRenderItem(renderItems = []) {
  return (Array.isArray(renderItems) ? renderItems : []).find(item => item?.selected || item?.active) || null
}
